<script lang="ts" setup>
  import { Typo, Flag, Tooltip, type FlagName } from '@travelata-libs/ui-kit';
  import { formatMoney, parseDate, formatDate, pluralize } from '@travelata-libs/utils';
  import { computed, ref } from 'vue';
  import { type SearchCriteriaHistory } from '@/entities/searchCriteria';

  interface HistorySearchCardProps {
    historySearch: SearchCriteriaHistory;
    isFirst?: boolean;
    isLast?: boolean;
  }

  const props = withDefaults(defineProps<HistorySearchCardProps>(), {
    isFirst: false,
    isLast: false,
  });

  const title = computed(() => {
    if (props.historySearch.price) {
      return `${props.historySearch.description} от ${formatMoney(props.historySearch.price)}`;
    }
    return `${props.historySearch.description}`;
  });

  const dateText = computed(() => {
    const checkInDateRange = props.historySearch.checkInDateRange;
    const nights = props.historySearch.nightRange;
    const dateRange = parseDate(checkInDateRange.from);
    const dateTo = parseDate(checkInDateRange.to);
    if (!dateRange || !dateTo) return '';
    return `с ${formatDate(dateRange, 'dd.MM')} по ${formatDate(dateTo, 'dd.MM')} • ${nights.from}–${nights.to} ночей`;
  });

  const touristsText = computed(() => {
    const touristGroup = props.historySearch.touristGroup;
    const departureCity = props.historySearch.departureCity;
    const count = touristGroup.adults + touristGroup.kids + touristGroup.infants;
    return `${pluralize(['турист', 'туриста', 'туристов'], count)} • ${departureCity.name}`;
  });

  const flagName = computed(() => {
    const id = props.historySearch.countries[0];
    return `${id}` as FlagName;
  });

  const titleRef = ref<{ $el: HTMLElement } | null>(null);
  const isHovered = ref(false);

  const isHintVisible = computed(() => {
    const element = titleRef.value?.$el;
    return isHovered.value && Boolean(element && element.scrollWidth > element.clientWidth);
  });

  const hintPlacement = computed(() => {
    if (props.isFirst) return 'top-start';
    if (props.isLast) return 'top-end';
    return 'top';
  });

  const onClick = () => {
    if (props.historySearch.url) {
      window.location.href = props.historySearch.url;
    }
  };
</script>

<template>
  <div
    class="history-search-card"
    @click="onClick"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div>
      <Flag
        class="history-search-card__flag"
        :name="flagName"
      />
    </div>
    <div class="history-search-card__container">
      <Typo
        ref="titleRef"
        variant="body-l-500"
        class="history-search-card__title"
      >
        {{ title }}
      </Typo>
      <Typo
        variant="body-s-400"
        class="history-search-card__date"
      >
        {{ dateText }}
      </Typo>
      <Typo
        variant="body-s-400"
        class="history-search-card__tourists"
      >
        {{ touristsText }}
      </Typo>
    </div>

    <Tooltip
      class="history-search-card__hint"
      trigger="manual"
      :placement="hintPlacement"
      width="max-content"
      :visible="isHintVisible"
    >
      <Typo
        variant="body-s-400"
        data-testid="history-search-card-hint"
      >
        {{ title }}
      </Typo>
    </Tooltip>
  </div>
</template>

<style lang="scss" scoped>
  .history-search-card {
    backdrop-filter: blur(14.6px);
    background: rgb(255 255 255 / 0.15);
    border-radius: var(--rounding-xl);
    box-shadow: var(--shadow-xxs);
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    gap: var(--spacing-s);
    padding: var(--spacing-m);
    position: relative;
    transition: var(--transition-default);
    width: 265px;

    &:hover {
      transform: translateY(-5px);
      z-index: 1;
    }

    &__hint {
      left: 0;
      position: absolute;
      right: 0;
      top: var(--spacing-m);
    }

    &__title {
      margin-bottom: var(--spacing-xxs);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__date {
      margin-bottom: var(--spacing-xxxs);
    }

    &__flag {
      border: 1px solid var(--color-bg-primary);
      border-radius: 100%;
      box-shadow: var(--shadow-xs);
    }

    &__container {
      color: var(--color-text-inverse);
      display: inline-flex;
      flex-direction: column;
      min-width: 0;
    }
  }
</style>
